import { Stack, Grid, Typography, Box } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useGetLanguageTestList, useGetAttachmentDocumentTypeList } from "@/api/services/global/master";
import { black } from "@/theme/colors";
import { CaseInfoItem } from "@/features/case/components/CaseBasicInfo/CaseInfoItem";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useMemo } from "react";
import { findLabelByValue } from "@/utils/object";
import { DATE_FORMAT } from "@/constants/datetime";
import dayjs from "dayjs";
import { ATTACHED_DOCUMENT_COLOR, CASE_MATCH_DOCUMENT_TYPE_IDS } from "@/types/enum";
import { EMPTY_STR } from "@/constants/string";
import { convertNumberToCommas } from "@/utils/input";
import { TalentFileItem } from "@/features/talent/components/talent-detail/TalentFileItem";
import { DocumentTypeTagsReadOnly, ResolvedDocumentTag } from "@/features/talent/components/talent-detail/DocumentTypeTagsReadOnly";
import { CaseMatchTagList } from "@/features/case/components/CaseMatchTagList";

type TCaseMatchDetailInfoProps = {
  caseMatch?: TCaseMatches;
  talent?: Partial<TTalentForm>;
};

export const CaseMatchDetailInfo: React.FC<TCaseMatchDetailInfoProps> = ({ caseMatch, talent }) => {
  const { getUserOptions } = useGetUserListPersonInCharge();
  const { languageTestLevelOptions } = useGetLanguageTestList();
  const { data: documentTypes } = useGetAttachmentDocumentTypeList();

  const documentTypeMap = useMemo(() => {
    const map = new Map<number, { color: string; documentTypeName: string }>();
    documentTypes.forEach((dt) => map.set(Number(dt.id), { color: dt.color, documentTypeName: dt.documentTypeName }));
    return map;
  }, [documentTypes]);

  const resolveTags = (typeIds?: number[] | null): ResolvedDocumentTag[] => {
    if (!typeIds) return [];
    return typeIds
      .map((typeId): ResolvedDocumentTag | null => {
        const info = documentTypeMap.get(typeId);
        if (!info) return null;
        const colorPair = ATTACHED_DOCUMENT_COLOR[info.color as keyof typeof ATTACHED_DOCUMENT_COLOR];
        const bgColor = colorPair?.bgColor || "#fff";
        const color = colorPair?.color || "#000000";
        return {
          id: typeId,
          label: info.documentTypeName,
          bgColor,
          color,
          hasBorder: bgColor === "#FFFFFF",
        };
      })
      .filter((tag): tag is ResolvedDocumentTag => tag !== null);
  };

  // Files that belong under "証明書など" — keep full tag list (including non-case-match tags) so all chips show.
  const certificateFiles = useMemo(() => {
    if (!talent?.talentUploadFiles || talent.talentUploadFiles.length === 0) return [];
    return talent.talentUploadFiles.filter((f) => f.attachmentDocumentTypeIds?.some((id) => CASE_MATCH_DOCUMENT_TYPE_IDS.includes(id)));
  }, [talent?.talentUploadFiles]);

  const casePersonInChargeName = useMemo(() => {
    const userId = caseMatch?.caseMatchUserId ?? caseMatch?.case?.personInChargeId;
    if (!userId) return EMPTY_STR.TEXT;
    const options = getUserOptions(userId);
    return findLabelByValue(options, userId) || EMPTY_STR.TEXT;
  }, [caseMatch, getUserOptions]);

  const talentPersonInChargeName = useMemo(() => {
    const userId = talent?.personInChargeId;
    if (!userId) return EMPTY_STR.TEXT;
    const options = getUserOptions(userId);
    return findLabelByValue(options, userId) || EMPTY_STR.TEXT;
  }, [talent, getUserOptions]);

  const proposalDate = caseMatch?.proposalDate ? dayjs(caseMatch.proposalDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const interviewDate = caseMatch?.interviewDate ? dayjs(caseMatch.interviewDate).format(DATE_FORMAT) : EMPTY_STR.DATE;

  const japaneseLanguageProficiency = useMemo(() => {
    if (!talent?.languageCertificates || talent.languageCertificates.length === 0) return EMPTY_STR.TEXT;
    const labels = talent.languageCertificates
      .map((cert) => {
        if (cert.languageTestLevelId) {
          const value = Number(cert.languageTestLevelId);
          return findLabelByValue(languageTestLevelOptions, value === -1 ? value : String(value));
        } else if (cert.languageTestId) {
          const testId = Number(cert.languageTestId);
          if (testId === 1) {
            return findLabelByValue(languageTestLevelOptions, -1);
          } else {
            return findLabelByValue(languageTestLevelOptions, String(testId));
          }
        }
        return null;
      })
      .filter(Boolean);
    return labels.length > 0 ? labels.join(", ") : EMPTY_STR.TEXT;
  }, [talent, languageTestLevelOptions]);

  const majorName = useMemo(() => {
    return talent?.major?.majorName || EMPTY_STR.TEXT;
  }, [talent]);

  const jobTypeName = useMemo(() => {
    return talent?.jobType?.jobName || EMPTY_STR.TEXT;
  }, [talent]);

  const companyOfferDate = caseMatch?.companyOfferDate ? dayjs(caseMatch.companyOfferDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const offerAcceptDate = caseMatch?.offerAcceptDate ? dayjs(caseMatch.offerAcceptDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const joinDateExpected = caseMatch?.joinDateExpected ? dayjs(caseMatch.joinDateExpected).format(DATE_FORMAT) : EMPTY_STR.DATE;
  const salesFee =
    caseMatch?.salesFee !== null && caseMatch?.salesFee !== undefined ? convertNumberToCommas(String(caseMatch.salesFee)) + "円" : EMPTY_STR.TEXT;
  const purchaseFee =
    caseMatch?.purchaseFee !== null && caseMatch?.purchaseFee !== undefined
      ? convertNumberToCommas(String(caseMatch.purchaseFee)) + "円"
      : EMPTY_STR.TEXT;
  const companyJoiningDate = caseMatch?.companyJoiningDate ? dayjs(caseMatch.companyJoiningDate).format(DATE_FORMAT) : EMPTY_STR.DATE;

  return (
    <InnerBox mt={3} p={3} showDivider={false} borderColor={black[95]} bordered={true}>
      <Stack>
        <Box mb={3}>
          <Typography variant="sub16Bold" color={black[30]}>
            マッチング情報
          </Typography>
        </Box>

        <Grid container rowSpacing={1} columnSpacing={3}>
          <Grid item xs={6}>
            <CaseInfoItem label="案件マッチング担当者" value={casePersonInChargeName} />
          </Grid>

          <Grid item xs={6}>
            <CaseInfoItem label="人材マッチング担当者" value={talentPersonInChargeName} />
          </Grid>

          <Grid item xs={6}>
            <CaseInfoItem label="推薦日" value={proposalDate} />
          </Grid>

          <Grid item xs={6}>
            <CaseInfoItem label="面接日" value={interviewDate} />
          </Grid>

          <Grid item xs={12}>
            <CaseInfoItem label="日本語能力証明書等（実習修了証明含む）" value={japaneseLanguageProficiency} />
          </Grid>

          <Grid item xs={12}>
            <CaseInfoItem label="特定技能合格証等" value={talent?.specificSkillCertificateStr || EMPTY_STR.TEXT} />
          </Grid>

          <Grid item xs={6} mt={1}>
            <CaseInfoItem label="技能実習　職種" value={majorName} />
          </Grid>

          <Grid item xs={6} mt={1}>
            <CaseInfoItem label="作業" value={jobTypeName} />
          </Grid>

          <Grid item xs={12} mt={1}>
            <Stack gap={1}>
              <Typography variant="cap12" color={black[70]}>
                タグ
              </Typography>
              <CaseMatchTagList tagIds={caseMatch?.tagIds} wrap />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack sx={{ borderBottom: "1px solid #E5E5E5", pb: 2, mt: 1, mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                <Typography variant="cap12" color={black[70]}>
                  証明書など
                </Typography>
              </Stack>
              {certificateFiles.length > 0 ? (
                <Stack>
                  {certificateFiles.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mt: idx === 0 ? 0 : 2,
                      }}
                    >
                      <TalentFileItem
                        currentFileIndex={idx}
                        files={certificateFiles}
                        filePath={item.filePath}
                        previewEnabled
                        downloadable={true}
                        maxWidth={336}
                        updatedAt={item.updatedAt}
                      />
                      <DocumentTypeTagsReadOnly tags={resolveTags(item.attachmentDocumentTypeIds)} />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body14Semi">{EMPTY_STR.TEXT}</Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <CaseInfoItem label="適合度メモ" value={caseMatch?.fitNote || EMPTY_STR.TEXT} type="textArea" />
          </Grid>

          <Grid item xs={6}>
            <CaseInfoItem label="企業内定日" value={companyOfferDate} />
          </Grid>

          <Grid item xs={6}>
            <CaseInfoItem label="内定受諾日" value={offerAcceptDate} />
          </Grid>

          <Grid item xs={12}>
            <CaseInfoItem label="企業入社日（予定）" value={joinDateExpected} />
          </Grid>

          <Grid item xs={6}>
            <CaseInfoItem label="紹介料（売上）" value={salesFee} />
          </Grid>

          <Grid item xs={6}>
            <CaseInfoItem label="紹介料（仕入）" value={purchaseFee} />
          </Grid>

          <Grid item xs={12}>
            <CaseInfoItem label="企業入社日" value={companyJoiningDate} isShowDivider={false} />
          </Grid>
        </Grid>
      </Stack>
    </InnerBox>
  );
};
